/**
 * MP Phase 3 — Audit tab panel for per-parcel provider variance.
 */
import React, { useEffect, useState } from 'react';
import type { SiteEntity } from '../../../types/site';
import type { SiteEvidenceDocument } from '../../../types/siteEvidence';
import { detectParcelVariance, type ParcelVarianceFinding } from '../../../lib/audit/parcelVarianceAuditor';

interface Props {
  site: SiteEntity;
}

export function ParcelVariancePanel({ site }: Props) {
  const [findings, setFindings] = useState<ParcelVarianceFinding[]>([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!site.sedId || site.sedStatus !== 'complete') {
        setFindings([]);
        return;
      }
      try {
        const { loadSedById } = await import('../../../services/sedLoader');
        const sed = await loadSedById(site.sedId);
        if (cancelled || !sed) return;
        setFindings(detectParcelVariance(sed as SiteEvidenceDocument));
      } catch {
        if (!cancelled) setFindings([]);
      }
    })();
    return () => { cancelled = true; };
  }, [site.sedId, site.sedStatus]);

  if (findings.length === 0) return null;

  return (
    <section className="rounded-lg border border-warning/30 bg-warning/5 p-4 space-y-2" data-testid="parcel-variance-panel">
      <h3 className="text-sm font-bold uppercase tracking-widest text-warning">
        Per-parcel variance ({findings.length})
      </h3>
      <p className="text-xs text-text-muted">
        One or more provider facts differ materially across parcels. Cite worst-case values or use per-parcel tables.
      </p>
      <ul className="space-y-2">
        {findings.map(f => (
          <li key={f.domain} className="text-xs text-text-primary border-l-2 border-warning pl-2">
            <span className="font-semibold uppercase tracking-wide">{f.domain}</span>
            {' — '}
            {f.message}
          </li>
        ))}
      </ul>
    </section>
  );
}
